const arrayBufferToBase64 = (bytes) =>
  btoa(String.fromCharCode(...new Uint8Array(bytes)));

const binaryStringToBase64 = (value) => btoa(value);

const getPublicKeyDer = (publicKey) =>
  Uint8Array.from(
    atob(publicKey.replace(/-----[^-]+-----/g, "").replace(/\s/g, "")),
    (character) => character.charCodeAt(0),
  );

async function encryptLoginCredentialsWithWebCrypto(publicKey, credentials) {
  const der = getPublicKeyDer(publicKey);
  const importedPublicKey = await window.crypto.subtle.importKey(
    "spki", der, { name: "RSA-OAEP", hash: "SHA-256" }, false, ["encrypt"],
  );
  const key = await window.crypto.subtle.generateKey(
    { name: "AES-GCM", length: 256 }, true, ["encrypt"],
  );
  const iv = window.crypto.getRandomValues(new Uint8Array(12));
  const ciphertext = await window.crypto.subtle.encrypt(
    { name: "AES-GCM", iv }, key,
    new TextEncoder().encode(JSON.stringify(credentials)),
  );
  const encryptedKey = await window.crypto.subtle.encrypt(
    { name: "RSA-OAEP" }, importedPublicKey,
    await window.crypto.subtle.exportKey("raw", key),
  );
  return {
    encrypted_key: arrayBufferToBase64(encryptedKey),
    iv: arrayBufferToBase64(iv),
    ciphertext: arrayBufferToBase64(ciphertext),
  };
}

async function encryptLoginCredentialsWithForge(publicKeyPem, credentials) {
  const forgeModule = await import("node-forge");
  const forge = forgeModule.default || forgeModule;
  const publicKey = forge.pki.publicKeyFromPem(publicKeyPem);
  const key = forge.random.getBytesSync(32);
  const iv = forge.random.getBytesSync(12);
  const cipher = forge.cipher.createCipher("AES-GCM", key);

  cipher.start({ iv, tagLength: 128 });
  cipher.update(forge.util.createBuffer(JSON.stringify(credentials), "utf8"));
  const cipherFinished = cipher.finish();

  if (!cipherFinished) throw new Error("No se pudo cifrar el login");

  const encryptedKey = publicKey.encrypt(key, "RSA-OAEP", {
    md: forge.md.sha256.create(),
    mgf1: {
      md: forge.md.sha256.create(),
    },
  });

  return {
    encrypted_key: binaryStringToBase64(encryptedKey),
    iv: binaryStringToBase64(iv),
    ciphertext: binaryStringToBase64(
      cipher.output.getBytes() + cipher.mode.tag.getBytes(),
    ),
  };
}

export async function encryptLoginCredentials(apiUri, credentials) {
  const response = await fetch(`${apiUri}/login/public-key`, { cache: "no-store" });
  if (!response.ok) throw new Error("No se pudo obtener la clave de login");
  const { public_key } = await response.json();

  if (window.crypto?.subtle) {
    console.info("[login-encryption] Using WebCrypto encryption.");
    return encryptLoginCredentialsWithWebCrypto(public_key, credentials);
  }

  if (window.location.protocol !== "http:") {
    throw new Error("WebCrypto no está disponible en un entorno seguro");
  }

  console.warn(
    "[login-encryption] WebCrypto is unavailable. Falling back to node-forge encryption. This is expected only in non-secure HTTP environments.",
  );
  return encryptLoginCredentialsWithForge(public_key, credentials);
}
